/**
 * AmbassadorInvites — the admin console for the Brand Ambassador program.
 * Two tabs:
 *   • Invites & QR   — mint recruitment QR codes / links to /ambassadors
 *     (?invite=CODE), each downloadable, with a visits → sign-ups funnel.
 *   • Applications   — review shoppers who applied from the home-page / dashboard
 *     tile: their TikTok / Instagram handles + agreements, with Approve (promotes
 *     to ambassador + opens the 12-month 2× window) / Reject.
 *
 * Talks to /api/admin/ambassador-invites and /api/admin/ambassador-applications.
 * Mounted at /admin/ambassadors.
 */
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import AmbassadorQRModal, { ambassadorInviteLink } from '../AmbassadorQRModal';
import { Icon } from './AdminIcons';

// ════════════════════════════ INVITES PANEL ══════════════════════════════════
function InviteStatusBadge({ invite }) {
  const now = Date.now();
  const expired = invite.expiresAt && new Date(invite.expiresAt).getTime() <= now;
  const full = invite.maxUses != null && invite.useCount >= invite.maxUses;
  if (!invite.isActive) return <span className="badge-gray">Paused</span>;
  if (expired) return <span className="badge-yellow">Expired</span>;
  if (full) return <span className="badge-yellow">Fully claimed</span>;
  return <span className="badge-green">Active</span>;
}

function InvitesPanel() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: '', maxUses: '', expiresAt: '', referredByEmail: '' });

  const load = () => {
    setLoading(true);
    api.get('/admin/ambassador-invites')
      .then((res) => setInvites(res.data))
      .catch(() => toast.error('Failed to load ambassador invites'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const body = {
        label: form.label || undefined,
        maxUses: form.maxUses ? Number(form.maxUses) : undefined,
        expiresAt: form.expiresAt || undefined,
        referredByEmail: form.referredByEmail || undefined,
      };
      const res = await api.post('/admin/ambassador-invites', body);
      setInvites((prev) => [res.data, ...prev]);
      setForm({ label: '', maxUses: '', expiresAt: '', referredByEmail: '' });
      toast.success(`Invite ${res.data.code} created`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (inv) => {
    try {
      const res = await api.patch(`/admin/ambassador-invites/${inv.id}`, { isActive: !inv.isActive });
      setInvites((prev) => prev.map((i) => (i.id === inv.id ? { ...i, ...res.data } : i)));
    } catch {
      toast.error('Failed to update invite');
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invite ${inv.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/ambassador-invites/${inv.id}`);
      setInvites((prev) => prev.filter((i) => i.id !== inv.id));
      toast.success('Invite deleted');
    } catch {
      toast.error('Failed to delete invite');
    }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(ambassadorInviteLink(code));
    toast.success('Invite link copied!');
  };

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const totalSignups = invites.reduce((s, i) => s + (i.useCount || 0), 0);
  const totalVisits = invites.reduce((s, i) => s + (i.visits || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-3">
        <div className="card text-center px-5 py-3">
          <div className="text-2xl font-bold text-tea-700">{totalVisits.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Landing visits</div>
        </div>
        <div className="card text-center px-5 py-3">
          <div className="text-2xl font-bold text-tea-700">{totalSignups.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Sign-ups</div>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="card">
        <h2 className="font-bold text-lg mb-4">Create an invite</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Label <span className="text-gray-400">(internal)</span></label>
            <input value={form.label} onChange={set('label')} placeholder="e.g. Spring IG push" className="input-field" maxLength={120} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max sign-ups <span className="text-gray-400">(optional)</span></label>
            <input type="number" min="1" value={form.maxUses} onChange={set('maxUses')} placeholder="Unlimited" className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expires <span className="text-gray-400">(optional)</span></label>
            <input type="date" value={form.expiresAt} onChange={set('expiresAt')} className="input-field" />
          </div>
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Attribute recruits to <span className="text-gray-400">(optional upline email)</span></label>
            <input type="email" value={form.referredByEmail} onChange={set('referredByEmail')} placeholder="recruiter@example.com — they earn on this ambassador's network" className="input-field" />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={creating} className="btn-primary w-full whitespace-nowrap">
              {creating ? 'Creating…' : 'Mint invite + QR'}
            </button>
          </div>
        </div>
      </form>

      {/* List */}
      <div className="card">
        <h2 className="font-bold text-lg mb-4">Invites</h2>
        {loading ? (
          <div className="py-10 text-center text-gray-400">Loading…</div>
        ) : invites.length === 0 ? (
          <div className="py-10 text-center text-gray-400">No invites yet — mint your first one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Code</th>
                  <th className="pb-2">Label</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2 text-center">Visits</th>
                  <th className="pb-2 text-center">Sign-ups</th>
                  <th className="pb-2">Attributes to</th>
                  <th className="pb-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0 align-middle">
                    <td className="py-3 font-mono font-bold text-tea-700">{inv.code}</td>
                    <td className="py-3 text-gray-600">{inv.label || <span className="text-gray-300">—</span>}</td>
                    <td className="py-3"><InviteStatusBadge invite={inv} /></td>
                    <td className="py-3 text-center">{(inv.visits || 0).toLocaleString()}</td>
                    <td className="py-3 text-center">
                      {(inv.useCount || 0).toLocaleString()}
                      {inv.maxUses != null && <span className="text-gray-400"> / {inv.maxUses}</span>}
                    </td>
                    <td className="py-3 text-gray-600">
                      {inv.referrer ? `${inv.referrer.firstName} ${inv.referrer.lastName}` : <span className="text-gray-300">Open</span>}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => setQr(inv)} className="text-tea-600 hover:underline text-xs font-medium">QR</button>
                        <button onClick={() => copyLink(inv.code)} className="text-tea-600 hover:underline text-xs font-medium">Copy link</button>
                        <button onClick={() => toggle(inv)} className="text-gray-500 hover:underline text-xs font-medium">
                          {inv.isActive ? 'Pause' : 'Activate'}
                        </button>
                        <button onClick={() => remove(inv)} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {qr && <AmbassadorQRModal code={qr.code} label={qr.label} onClose={() => setQr(null)} />}
    </div>
  );
}

// ══════════════════════════ APPLICATIONS PANEL ═══════════════════════════════
function AppStatusBadge({ status }) {
  if (status === 'approved') return <span className="badge-green">Approved</span>;
  if (status === 'rejected') return <span className="badge-red">Rejected</span>;
  return <span className="badge-yellow">Pending</span>;
}

function Social({ platform, handle }) {
  if (!handle) return <span className="text-gray-300">—</span>;
  const base = platform === 'tiktok' ? 'https://tiktok.com/@' : 'https://instagram.com/';
  const icon = platform === 'tiktok' ? 'sparkles' : 'media';
  return (
    <a href={base + handle} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-tea-600 hover:underline">
      <Icon name={icon} className="w-4 h-4" />@{handle}
    </a>
  );
}

function ApplicationsPanel() {
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = (status = filter) => {
    setLoading(true);
    const qs = status === 'all' ? '' : `?status=${status}`;
    api.get(`/admin/ambassador-applications${qs}`)
      .then((res) => { setApps(res.data.applications); setCounts(res.data.counts); })
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const review = async (app, action) => {
    let adminNote;
    if (action === 'reject') {
      adminNote = window.prompt('Optional note to the applicant (why it was declined):', '') || undefined;
    } else if (!window.confirm(`Approve ${app.applicant?.firstName}? They'll become an ambassador with a doubled first year.`)) {
      return;
    }
    setBusyId(app.id);
    try {
      const res = await api.patch(`/admin/ambassador-applications/${app.id}`, { action, adminNote });
      // Refresh counts + drop the row if it no longer matches the active filter.
      setCounts((c) => {
        const next = { ...c };
        if (app.status === 'pending') next.pending = Math.max(0, next.pending - 1);
        next[res.data.status] = (next[res.data.status] || 0) + 1;
        return next;
      });
      setApps((prev) => prev
        .map((a) => (a.id === app.id ? res.data : a))
        .filter((a) => filter === 'all' || a.status === filter));
      toast.success(action === 'approve' ? 'Approved — they are now a Brand Ambassador' : 'Application declined');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update application');
    } finally {
      setBusyId(null);
    }
  };

  const TABS = [
    { key: 'pending',  label: `Pending (${counts.pending})` },
    { key: 'approved', label: `Approved (${counts.approved})` },
    { key: 'rejected', label: `Rejected (${counts.rejected})` },
    { key: 'all',      label: 'All' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            className={'px-4 py-1.5 rounded-full text-sm font-medium transition-colors ' +
              (filter === t.key ? 'bg-tea-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="py-10 text-center text-gray-400">Loading…</div>
        ) : apps.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            {filter === 'pending' ? 'No applications waiting for review.' : 'Nothing here.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Applicant</th>
                  <th className="pb-2">TikTok</th>
                  <th className="pb-2">Instagram</th>
                  <th className="pb-2">Applied</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2 text-right">Review</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => (
                  <tr key={app.id} className="border-b last:border-0 align-middle">
                    <td className="py-3">
                      <div className="font-semibold text-gray-900">
                        {app.applicant ? `${app.applicant.firstName} ${app.applicant.lastName}` : 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">{app.applicant?.email}</div>
                    </td>
                    <td className="py-3"><Social platform="tiktok" handle={app.tiktokHandle} /></td>
                    <td className="py-3"><Social platform="instagram" handle={app.instagramHandle} /></td>
                    <td className="py-3 text-gray-500">{new Date(app.createdAt).toLocaleDateString()}</td>
                    <td className="py-3"><AppStatusBadge status={app.status} /></td>
                    <td className="py-3">
                      {app.status === 'pending' ? (
                        <div className="flex items-center justify-end gap-2">
                          <button disabled={busyId === app.id} onClick={() => review(app, 'approve')}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
                            Approve
                          </button>
                          <button disabled={busyId === app.id} onClick={() => review(app, 'reject')}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div className="text-right text-xs text-gray-400">
                          {app.reviewer ? `by ${app.reviewer.firstName}` : '—'}
                          {app.adminNote && <div className="text-gray-400 italic mt-0.5 max-w-[200px] truncate" title={app.adminNote}>“{app.adminNote}”</div>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════ SHELL ══════════════════════════════════════
export default function AmbassadorInvites() {
  const [tab, setTab] = useState('applications');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Icon name="ambassadors" className="w-6 h-6 text-tea-500" /> Brand Ambassadors</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Review applicants from the{' '}
          <a href="/ambassadors" target="_blank" rel="noreferrer" className="text-tea-600 hover:underline">ambassador landing page</a>,
          and mint QR codes &amp; invite links. Approved ambassadors earn a doubled 6% first year.
        </p>
      </div>

      {/* Primary tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { key: 'applications', label: 'Applications' },
          { key: 'invites', label: 'Invites & QR' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ' +
              (tab === t.key ? 'border-tea-600 text-tea-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'applications' ? <ApplicationsPanel /> : <InvitesPanel />}
    </div>
  );
}
